import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  Calendar,
  ChevronDown,
  CheckCircle2,
  Lock,
  Sparkles,
  Target,
} from "lucide-react";
import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { flattenProductRoadmapDays } from "../utils/buildProductRoadmap";
import { trackProductEvent } from "../lib/analytics";
import { useGlobalChat } from "../context/GlobalChatContext";

const getDateKey = (date) => {
  const local = new Date(date);
  local.setHours(0, 0, 0, 0);
  return local.toISOString().slice(0, 10);
};

const getCurrentAbsoluteDay = (startedAt) => {
  if (!startedAt) return 1;
  const start = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);
  start.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.max(
    Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1,
    1
  );
};

const getPhaseKey = (phase) => phase?.phaseKey || phase?.id || String(phase?.order || "");
const getWeekKey = (phase, week) =>
  [getPhaseKey(phase), week?.week || week?.id || week?.title || ""].join(":");
const getDayKey = (phase, week, day) =>
  [getWeekKey(phase, week), day?.id || day?.title || day?.absoluteIndex || ""].join(":");

const normalizePlanType = (value = "") => {
  const text = String(value || "").trim().toLowerCase();
  if (!text) return "";
  if (text.includes("product")) return "product-development";
  if (text.includes("interview") || text.includes("job") || text.includes("prep")) {
    return "interview-prep";
  }
  return "learning-roadmap";
};

const hasProductDevelopmentAccess = (userData = {}) => {
  if (userData?.plan !== "PRO") return false;

  const resolvedType = normalizePlanType(
    userData?.activePlanType ||
      userData?.productLine ||
      userData?.planType ||
      userData?.activePlanTitle
  );

  return resolvedType === "product-development";
};

export default function MyProductRoadmapPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { openChat } = useGlobalChat();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completingKey, setCompletingKey] = useState("");
  const [data, setData] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [showReminder, setShowReminder] = useState(false);
  const [showCalendarSuccess, setShowCalendarSuccess] = useState(false);
  const [emailReminderEnabled, setEmailReminderEnabled] = useState(false);
  const [calendarReminderEnabled, setCalendarReminderEnabled] = useState(false);
  const [calendarTime, setCalendarTime] = useState("07:30");
  const [openPhase, setOpenPhase] = useState(null);
  const [openWeek, setOpenWeek] = useState(null);
  const [openDay, setOpenDay] = useState(null);

  const getGoogleCalendarCallbackUrl = () =>
    "https://us-central1-skilll-dev.cloudfunctions.net/googleCalendarCallback";

  const getGoogleAuthUrl = () => {
    const params = new URLSearchParams({
      client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
      redirect_uri: getGoogleCalendarCallbackUrl(),
      response_type: "code",
      access_type: "offline",
      prompt: "consent",
      include_granted_scopes: "true",
      scope: "https://www.googleapis.com/auth/calendar.events",
      state: user.uid,
    });

    return `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
  };

  useEffect(() => {
    if (!user?.uid) return;

    const load = async () => {
      try {
        setLoading(true);
        const snap = await getDoc(doc(db, "product_roadmaps", user.uid));
        setData(snap.exists() ? snap.data() : null);
      } catch (error) {
        console.error("Failed to load product roadmap", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [user?.uid]);

  useEffect(() => {
    if (!user?.uid) return;

    const loadAccess = async () => {
      const snap = await getDoc(doc(db, "users", user.uid));
      if (!snap.exists()) return;

      const userData = snap.data();
      setIsPro(hasProductDevelopmentAccess(userData));
      setCalendarConnected(
        !!userData.calendarConnected && !!userData.calendarRefreshToken
      );
    };

    loadAccess();
  }, [user?.uid]);

  useEffect(() => {
    if (!showReminder || !user?.uid) return;

    const loadSettings = async () => {
      const snap = await getDoc(doc(db, "product_roadmaps", user.uid));
      if (!snap.exists()) return;

      const roadmapData = snap.data();
      setEmailReminderEnabled(roadmapData.emailReminderEnabled ?? false);
      setCalendarReminderEnabled(roadmapData.calendarReminderEnabled ?? false);
      setCalendarTime(roadmapData.calendarReminderTime || "07:30");
    };

    loadSettings();
  }, [showReminder, user?.uid]);

  const flatDays = useMemo(
    () => flattenProductRoadmapDays(data?.roadmapRef),
    [data?.roadmapRef]
  );
  const completedDays = useMemo(
    () => data?.progress?.completedDays || {},
    [data?.progress?.completedDays]
  );
  const completedCount = Object.keys(completedDays).length;
  const totalDays = flatDays.length;
  const progressPercent = totalDays
    ? Math.round((completedCount / totalDays) * 100)
    : 0;
  const currentDayNumber = useMemo(
    () => getCurrentAbsoluteDay(data?.consistency?.startedAt),
    [data?.consistency?.startedAt]
  );
  const isStarted = !!data?.consistency?.startedAt;

  const roadmapWithDates = useMemo(() => {
    const startedAt = data?.consistency?.startedAt
      ? data.consistency.startedAt.toDate
        ? data.consistency.startedAt.toDate()
        : new Date(data.consistency.startedAt)
      : null;

    return flatDays.map((day, index) => {
      const date = startedAt
        ? new Date(
            startedAt.getFullYear(),
            startedAt.getMonth(),
            startedAt.getDate() + index
          )
        : null;
      const dateKey = date ? getDateKey(date) : null;
      const completed = dateKey ? !!completedDays[dateKey] : false;
      const unlocked = !startedAt ? index === 0 : index <= currentDayNumber - 1;
      return {
        ...day,
        date,
        dateKey,
        completed,
        unlocked,
      };
    });
  }, [flatDays, data?.consistency?.startedAt, completedDays, currentDayNumber]);

  const currentDayItem =
    roadmapWithDates[Math.max(currentDayNumber - 1, 0)] ||
    roadmapWithDates[0] ||
    null;

  useEffect(() => {
    if (!data?.roadmapRef?.phases?.length) return;

    const currentPhase = data.roadmapRef.phases.find((phase) =>
      phase.weeks?.some((week) =>
        roadmapWithDates.some(
          (item) =>
            item.weekNumber === week.week &&
            item.phaseOrder === phase.order &&
            item.absoluteIndex === currentDayItem?.absoluteIndex
        )
      )
    ) || data.roadmapRef.phases[0];

    const phaseKey = getPhaseKey(currentPhase);
    setOpenPhase((prev) => prev || phaseKey);

    if (!currentDayItem) return;

    const phase = data.roadmapRef.phases.find((item) => item.order === currentDayItem.phaseOrder) || currentPhase;
    const week = phase?.weeks?.find((item) => item.week === currentDayItem.weekNumber) || phase?.weeks?.[0];

    if (phase && week) {
      setOpenWeek((prev) => prev || getWeekKey(phase, week));
      setOpenDay((prev) => prev || getDayKey(phase, week, currentDayItem));
    }
  }, [data?.roadmapRef?.phases, roadmapWithDates, currentDayItem]);

  const openProductAssistant = (item) => {
    if (!item) return;

    openChat({
      assistantMode: "product-development",
      dayContext: {
        title: item.title,
        topic: item.topic,
        objective: item.objective,
        deliverable: item.deliverable,
        checklist: item.checklist || [],
        howTo: item.howTo || [],
        references: item.references || [],
        featureFocus: item.weekFeatureFocus || "",
      },
    });
  };

  const startJourney = async () => {
    if (!user?.uid || saving || isStarted) return;
    try {
      setSaving(true);
      await updateDoc(doc(db, "product_roadmaps", user.uid), {
        "consistency.startedAt": serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      void trackProductEvent("product_roadmap_started", {}, { user });
      const snap = await getDoc(doc(db, "product_roadmaps", user.uid));
      setData(snap.exists() ? snap.data() : null);
    } catch (error) {
      console.error("Failed to start product roadmap", error);
      alert("Unable to start right now");
    } finally {
      setSaving(false);
    }
  };

  const markDayComplete = async (item) => {
    if (!user?.uid || !item.dateKey || item.completed || !item.unlocked) return;
    try {
      setCompletingKey(item.dateKey);
      await updateDoc(doc(db, "product_roadmaps", user.uid), {
        [`progress.completedDays.${item.dateKey}`]: true,
        updatedAt: serverTimestamp(),
      });
      void trackProductEvent(
        "product_roadmap_day_completed",
        { dayId: item.id, weekNumber: item.weekNumber },
        { user }
      );
      setData((prev) => ({
        ...prev,
        progress: {
          ...(prev?.progress || {}),
          completedDays: {
            ...(prev?.progress?.completedDays || {}),
            [item.dateKey]: true,
          },
        },
      }));
    } catch (error) {
      console.error("Failed to complete product roadmap day", error);
      alert("Unable to mark this day complete right now");
    } finally {
      setCompletingKey("");
    }
  };

  const saveReminderTime = async () => {
    if (!user?.uid) return;

    try {
      setSaving(true);

      await updateDoc(doc(db, "product_roadmaps", user.uid), {
        emailReminderEnabled,
        calendarReminderEnabled,
        calendarReminderTime: calendarReminderEnabled
          ? calendarTime || "07:30"
          : null,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        updatedAt: serverTimestamp(),
      });

      setShowReminder(false);

      if (calendarReminderEnabled && calendarConnected) {
        setShowCalendarSuccess(true);
      } else if (calendarReminderEnabled && !calendarConnected) {
        window.location.href = getGoogleAuthUrl();
      }
    } catch (error) {
      console.error("Failed to save product reminder settings", error);
      alert("Failed to save reminder settings right now.");
    } finally {
      setSaving(false);
    }
  };

  const handlePremiumNav = (path) => {
    if (!isPro) {
      navigate("/pricing");
      return;
    }
    navigate(path);
  };

  if (loading) {
    return (
      <Page>
        <Shell>
          <LoadingHeroCard>
            <SkeletonPill />
            <SkeletonTitle />
            <SkeletonText $w="72%" />
            <SkeletonText $w="56%" />
            <LoadingStatGrid>
              {Array.from({ length: 4 }).map((_, index) => (
                <LoadingStatCard key={index}>
                  <SkeletonText $w="44%" />
                  <SkeletonValue />
                  <SkeletonText $w="82%" />
                </LoadingStatCard>
              ))}
            </LoadingStatGrid>
          </LoadingHeroCard>

          <LoadingSectionCard>
            <SkeletonPill />
            <SkeletonSectionTitle />
            <LoadingList>
              {Array.from({ length: 3 }).map((_, index) => (
                <LoadingListCard key={index}>
                  <SkeletonText $w="18%" />
                  <SkeletonText $w="66%" />
                  <SkeletonText $w="88%" />
                  <SkeletonText $w="74%" />
                </LoadingListCard>
              ))}
            </LoadingList>
          </LoadingSectionCard>
        </Shell>
      </Page>
    );
  }

  if (!data) {
    return (
      <Page>
        <Shell>
          <EmptyCard>
            <Sparkles size={22} />
            <h1>No product roadmap yet</h1>
            <p>Create your free product development roadmap and then unlock the consistency system around it.</p>
            <PrimaryButton onClick={() => navigate("/create-product-roadmap")}>
              Create Product Roadmap <ArrowRight size={16} />
            </PrimaryButton>
          </EmptyCard>
        </Shell>
      </Page>
    );
  }

  return (
    <Page>
      <Shell
        as={motion.section}
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, ease: "easeOut" }}
      >
        <Hero>
          <HeroMain>
            <Eyebrow>
              <Brief>
                <Sparkles size={14} />
                AI Product Builder
              </Brief>
            </Eyebrow>
            <Title>{data.product?.productName}</Title>
            <Sub>{data.product?.productIdea}</Sub>
            <MetaRow>
              <MetaPill>{data.product?.platform}</MetaPill>
              <MetaPill>{data.product?.technology}</MetaPill>
              <MetaPill>{data.product?.timelineWeeks} weeks</MetaPill>
            </MetaRow>

            <ActionRow>
              {!isStarted ? (
                <PrimaryButton onClick={startJourney} disabled={saving}>
                  {saving ? "Starting..." : "Start Self Internship"}
                  <ArrowRight size={16} />
                </PrimaryButton>
              ) : !isPro ? (
                <PrimaryButton onClick={() => navigate("/pricing")}>
                  Unlock consistency system <ArrowRight size={16} />
                </PrimaryButton>
              ) : null}
              <SecondaryButton onClick={() => navigate("/create-product-roadmap")}>
                Rebuild roadmap
              </SecondaryButton>
              <SecondaryButton
                onClick={() => {
                  if (!isPro) {
                    navigate("/pricing");
                    return;
                  }
                  setShowReminder(true);
                }}
              >
                Reminder Settings
              </SecondaryButton>
              <SecondaryButton onClick={() => handlePremiumNav("/product-roadmap/calendar")}>
                Calendar View
              </SecondaryButton>
              <SecondaryButton onClick={() => handlePremiumNav("/product-dashboard")}>
                Dashboard
              </SecondaryButton>
            </ActionRow>
          </HeroMain>

          <HeroSide>
            <MetricCard>
              <span>Current focus</span>
              <strong>
                {isStarted
                  ? currentDayItem?.title || "Open today’s build"
                  : "Journey not started"}
              </strong>
            </MetricCard>
            <MetricCard>
              <span>Progress</span>
              <strong>{progressPercent}% complete</strong>
            </MetricCard>
            <MetricCard>
              <span>Technology</span>
              <strong>{data.product?.technology}</strong>
            </MetricCard>
          </HeroSide>
        </Hero>

        {currentDayItem && (
          <TodayCard>
            <TodayCardHeader>
              <div>
                <SectionEyebrow>
                  {isStarted ? "Today’s build" : "Your first build day"}
                </SectionEyebrow>
                <TodayTitle>
                  {currentDayItem.weekFeatureFocus
                    ? `${currentDayItem.weekFeatureFocus}`
                    : currentDayItem.title}
                </TodayTitle>
                <TodaySummary>
                  {isStarted
                    ? "Open only today’s task and ship one meaningful piece of the product."
                    : "Start your self internship and this becomes your first focused build day."}
                </TodaySummary>
              </div>
              <TodayDayBadge>
                Day {currentDayItem.absoluteIndex + 1}
              </TodayDayBadge>
            </TodayCardHeader>

            <TodayGrid>
              <TodayBlock>
                <TodayLabel>Today’s feature</TodayLabel>
                <strong>
                  {currentDayItem.weekFeatureFocus ||
                    currentDayItem.topic ||
                    currentDayItem.title}
                </strong>
                <p>{currentDayItem.objective}</p>
              </TodayBlock>
              <TodayBlock>
                <TodayLabel>Today’s task</TodayLabel>
                <strong>{currentDayItem.title}</strong>
                <p>{currentDayItem.deliverable}</p>
              </TodayBlock>
              <TodayBlock>
                <TodayLabel>How to do it</TodayLabel>
                <MiniList>
                  {(currentDayItem.howTo || []).slice(0, 3).map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </MiniList>
              </TodayBlock>
              <TodayBlock>
                <TodayLabel>Reference docs</TodayLabel>
                {!!currentDayItem.references?.length ? (
                  <ReferenceList>
                    {currentDayItem.references.map((ref) => (
                      <li key={ref.url}>
                        <ReferenceLink href={ref.url} target="_blank" rel="noreferrer">
                          {ref.title}
                        </ReferenceLink>
                      </li>
                    ))}
                  </ReferenceList>
                ) : (
                  <p>Task-specific docs will appear here once the roadmap is generated.</p>
                )}
              </TodayBlock>
            </TodayGrid>

            <TodayActions>
              <AIHelperButton
                onClick={() => openProductAssistant(currentDayItem)}
              >
                <Sparkles size={14} />
                Ask AI Daily Development Assistant
              </AIHelperButton>

              {isStarted && !currentDayItem.completed && (
                <CompleteButton
                  disabled={
                    !currentDayItem.unlocked ||
                    completingKey === currentDayItem.dateKey
                  }
                  onClick={() => markDayComplete(currentDayItem)}
                >
                  {completingKey === currentDayItem.dateKey
                    ? "Completing..."
                    : "Mark today done"}
                </CompleteButton>
              )}
              {isStarted && (
                <SecondaryButton
                  onClick={() => handlePremiumNav(`/product-tasks-board/day?dayId=${currentDayItem.id}`)}
                >
                  Open Task Board
                </SecondaryButton>
              )}
            </TodayActions>
          </TodayCard>
        )}

        <StatsGrid>
          <StatCard>
            <span>Total phases</span>
            <strong>{data.roadmapRef?.phases?.length || 0}</strong>
          </StatCard>
          <StatCard>
            <span>Total days</span>
            <strong>{totalDays}</strong>
          </StatCard>
          <StatCard>
            <span>Completed days</span>
            <strong>{completedCount}</strong>
          </StatCard>
          <StatCard>
            <span>Technology</span>
            <strong>{data.product?.technology}</strong>
          </StatCard>
        </StatsGrid>

        <FeatureCard>
          <SectionEyebrow>Core features</SectionEyebrow>
          <SectionTitle>What this roadmap will build</SectionTitle>
          <FeatureList>
            {String(data.product?.coreFeatures || "")
              .split(",")
              .map((item) => item.trim())
              .filter(Boolean)
              .map((feature) => (
                <FeaturePill key={feature}>{feature}</FeaturePill>
              ))}
          </FeatureList>
        </FeatureCard>

        <ConsistencyCard>
          <ConsistencyHeader>
            <div>
              <SectionEyebrow>Paid consistency system</SectionEyebrow>
              <SectionTitle>Turn this roadmap into a daily execution loop</SectionTitle>
            </div>
            <LockedBadge>
              {isPro ? <Sparkles size={12} /> : <Lock size={12} />}
              {isPro ? "Active" : "Premium layer"}
            </LockedBadge>
          </ConsistencyHeader>
          <ConsistencyGrid>
            <ConsistencyItem>
              <Calendar size={18} />
              <strong>Email reminders + Google Calendar sync</strong>
              <p>Keep the product roadmap alive in your real calendar instead of letting it sit unused.</p>
            </ConsistencyItem>
            <ConsistencyItem>
              <Target size={18} />
              <strong>Daily product task board</strong>
              <p>Turn the roadmap into daily execution with visible TODO, in-progress, and done states.</p>
            </ConsistencyItem>
            <ConsistencyItem>
              <Sparkles size={18} />
              <strong>AI Daily Development Assistant</strong>
              <p>Get a roadmap-aware answer for what to build today, what to cut, and how to stay on track.</p>
            </ConsistencyItem>
          </ConsistencyGrid>
          <ConsistencyActions>
            <SecondaryButton
              onClick={() => (isPro ? setShowReminder(true) : navigate("/pricing"))}
            >
              {isPro ? "Reminder Settings" : "Unlock Reminders"}
            </SecondaryButton>
            <SecondaryButton
              onClick={() =>
                isPro
                  ? navigate("/product-roadmap/calendar")
                  : navigate("/pricing")
              }
            >
              {isPro ? "Calendar View" : "Unlock Calendar View"}
            </SecondaryButton>
            <SecondaryButton
              onClick={() =>
                isPro ? navigate("/product-dashboard") : navigate("/pricing")
              }
            >
              {isPro ? "Product Dashboard" : "Unlock Dashboard"}
            </SecondaryButton>
            <PrimaryButton onClick={() => navigate(isPro ? "/my-product-roadmap" : "/pricing")}>
              {isPro ? "Consistency system active" : "View Product Development Pricing"}
            </PrimaryButton>
          </ConsistencyActions>
        </ConsistencyCard>

        <PhaseStack>
          {data.roadmapRef?.phases?.map((phase) => (
            <PhaseCard key={phase.phaseKey}>
              <PhaseHeader
                onClick={() =>
                  setOpenPhase((prev) =>
                    prev === getPhaseKey(phase) ? null : getPhaseKey(phase)
                  )
                }
              >
                <PhaseHeaderMain>
                  <div>
                    <SectionEyebrow>Phase {phase.order}</SectionEyebrow>
                    <SectionTitle>{phase.title}</SectionTitle>
                  </div>
                  <PhaseSummary>{phase.summary}</PhaseSummary>
                </PhaseHeaderMain>
                <PhaseHeaderRight>
                  <ChevronDownWrap
                    animate={{ rotate: openPhase === getPhaseKey(phase) ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    <ChevronDown size={18} />
                  </ChevronDownWrap>
                </PhaseHeaderRight>
              </PhaseHeader>

              <AnimatePresence initial={false}>
                {openPhase === getPhaseKey(phase) && (
                  <WeekStack
                    as={motion.div}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.24, ease: "easeOut" }}
                  >
                    {phase.weeks.map((week) => {
                      const weekDays = roadmapWithDates.filter(
                        (item) =>
                          item.weekNumber === week.week &&
                          item.phaseOrder === phase.order
                      );
                      const weekKey = getWeekKey(phase, week);

                      return (
                        <WeekCard key={`${phase.phaseKey}-${week.week}`}>
                          <WeekHead
                            onClick={() =>
                              setOpenWeek((prev) => (prev === weekKey ? null : weekKey))
                            }
                          >
                            <div>
                              <strong>Week {week.week}</strong>
                              <p>{week.title}</p>
                              {week.featureFocus && (
                                <FeatureFocus>Feature focus: {week.featureFocus}</FeatureFocus>
                              )}
                            </div>
                            <WeekHeadRight>
                              <small>{weekDays.length} days</small>
                              <ChevronDownWrap
                                animate={{ rotate: openWeek === weekKey ? 180 : 0 }}
                                transition={{ duration: 0.2, ease: "easeOut" }}
                              >
                                <ChevronDown size={18} />
                              </ChevronDownWrap>
                            </WeekHeadRight>
                          </WeekHead>

                          <AnimatePresence initial={false}>
                            {openWeek === weekKey && (
                              <DayGrid
                                as={motion.div}
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: "auto" }}
                                exit={{ opacity: 0, height: 0 }}
                                transition={{ duration: 0.22, ease: "easeOut" }}
                              >
                                {weekDays.map((item) => {
                                  const dayKey = getDayKey(phase, week, item);
                                  const isOpen = openDay === dayKey;
                                  return (
                                    <DayCard key={item.id} $locked={!item.unlocked}>
                                      <DayHeader
                                        onClick={() =>
                                          setOpenDay((prev) => (prev === dayKey ? null : dayKey))
                                        }
                                      >
                                        <DayHeaderLeft>
                                          <span>Day {item.absoluteIndex + 1}</span>
                                          <strong>{item.title}</strong>
                                          <TopicLine>{item.topic}</TopicLine>
                                          <p>{item.objective}</p>
                                        </DayHeaderLeft>
                                        <ChevronDownWrap
                                          animate={{ rotate: isOpen ? 180 : 0 }}
                                          transition={{ duration: 0.2, ease: "easeOut" }}
                                        >
                                          <ChevronDown size={18} />
                                        </ChevronDownWrap>
                                      </DayHeader>

                                      <AnimatePresence initial={false}>
                                        {isOpen && (
                                          <DayContent
                                            as={motion.div}
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.2, ease: "easeOut" }}
                                          >
                                            {item.deliverable && (
                                              <Deliverable>
                                                Deliverable: <strong>{item.deliverable}</strong>
                                              </Deliverable>
                                            )}
                                            {!!item.howTo?.length && (
                                              <GuidanceBlock>
                                                <GuidanceTitle>How to do it</GuidanceTitle>
                                                <Checklist>
                                                  {item.howTo.map((point) => (
                                                    <li key={point}>{point}</li>
                                                  ))}
                                                </Checklist>
                                              </GuidanceBlock>
                                            )}
                                            {!!item.checklist?.length && (
                                              <GuidanceBlock>
                                                <GuidanceTitle>Checklist</GuidanceTitle>
                                                <Checklist>
                                                  {item.checklist.map((point) => (
                                                    <li key={point}>{point}</li>
                                                  ))}
                                                </Checklist>
                                              </GuidanceBlock>
                                            )}
                                            {!!item.references?.length && (
                                              <GuidanceBlock>
                                                <GuidanceTitle>Reference docs</GuidanceTitle>
                                                <ReferenceList>
                                                  {item.references.map((ref) => (
                                                    <li key={ref.url}>
                                                      <ReferenceLink
                                                        href={ref.url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                      >
                                                        {ref.title}
                                                      </ReferenceLink>
                                                    </li>
                                                  ))}
                                                </ReferenceList>
                                              </GuidanceBlock>
                                            )}
                                            <GuidanceBlock>
                                              <AIHelperButton
                                                onClick={() => openProductAssistant(item)}
                                              >
                                                <Sparkles size={14} />
                                                Ask AI Daily Development Assistant
                                              </AIHelperButton>
                                              <SecondaryButton
                                                onClick={() =>
                                                  handlePremiumNav(
                                                    `/product-tasks-board/day?dayId=${item.id}`
                                                  )
                                                }
                                              >
                                                Open Task Board
                                              </SecondaryButton>
                                            </GuidanceBlock>
                                            <DayFooter>
                                              <DayTag>{item.estimatedTime}</DayTag>
                                              {item.completed ? (
                                                <DoneState>
                                                  <CheckCircle2 size={14} />
                                                  Done
                                                </DoneState>
                                              ) : (
                                                <CompleteButton
                                                  disabled={
                                                    !item.unlocked || completingKey === item.dateKey
                                                  }
                                                  onClick={() => markDayComplete(item)}
                                                >
                                                  {completingKey === item.dateKey
                                                    ? "Completing..."
                                                    : item.unlocked
                                                    ? "Mark done"
                                                    : "Locked"}
                                                </CompleteButton>
                                              )}
                                            </DayFooter>
                                          </DayContent>
                                        )}
                                      </AnimatePresence>
                                    </DayCard>
                                  );
                                })}
                              </DayGrid>
                            )}
                          </AnimatePresence>
                        </WeekCard>
                      );
                    })}
                  </WeekStack>
                )}
              </AnimatePresence>
            </PhaseCard>
          ))}
        </PhaseStack>
      </Shell>

      {showReminder && (
        <ModalOverlay onClick={() => setShowReminder(false)}>
          <ReminderModal onClick={(e) => e.stopPropagation()}>
            <ReminderHeader>
              <div>
                <SectionEyebrow>Consistency settings</SectionEyebrow>
                <SectionTitle>Product roadmap reminders</SectionTitle>
              </div>
              <CloseButton onClick={() => setShowReminder(false)}>✕</CloseButton>
            </ReminderHeader>

            <ReminderBody>
              <SettingRow>
                <SettingCopy>
                  <h4>Email reminders</h4>
                  <p>Save your preference for daily product development reminder emails.</p>
                </SettingCopy>
                <ToggleShell>
                  <input
                    type="checkbox"
                    checked={emailReminderEnabled}
                    onChange={() => setEmailReminderEnabled((value) => !value)}
                  />
                </ToggleShell>
              </SettingRow>

              <Divider />

              <SettingRow>
                <SettingCopy>
                  <h4>Google Calendar reminder</h4>
                  <p>Save a preferred reminder time and connect Google Calendar for later sync support.</p>
                </SettingCopy>
                <ToggleShell>
                  <input
                    type="checkbox"
                    checked={calendarReminderEnabled}
                    onChange={() => setCalendarReminderEnabled((value) => !value)}
                  />
                </ToggleShell>
              </SettingRow>

              {calendarReminderEnabled && (
                <>
                  <SettingLabel>Reminder time</SettingLabel>
                  <TimeInput
                    type="time"
                    value={calendarTime}
                    onChange={(e) => setCalendarTime(e.target.value)}
                  />
                  <ReminderInfo>
                    {calendarConnected
                      ? "Google Calendar is already connected for this account."
                      : "Google Calendar is not connected yet. After saving, you can connect the account."}
                  </ReminderInfo>
                  {!calendarConnected && (
                    <PrimaryButton onClick={() => {
                      window.location.href = getGoogleAuthUrl();
                    }}>
                      Connect Google Calendar
                    </PrimaryButton>
                  )}
                </>
              )}

              <ReminderInfo>
                Settings are now stored on your product roadmap. Product-specific reminder delivery and sync can build on top of these preferences.
              </ReminderInfo>
            </ReminderBody>

            <ReminderFooter>
              <SecondaryButton onClick={() => setShowReminder(false)}>
                Cancel
              </SecondaryButton>
              <PrimaryButton onClick={saveReminderTime} disabled={saving}>
                {saving ? "Saving..." : "Save Settings"}
              </PrimaryButton>
            </ReminderFooter>
          </ReminderModal>
        </ModalOverlay>
      )}

      {showCalendarSuccess && (
        <ModalOverlay onClick={() => setShowCalendarSuccess(false)}>
          <ReminderModal onClick={(e) => e.stopPropagation()}>
            <ReminderHeader>
              <div>
                <SectionEyebrow>Saved</SectionEyebrow>
                <SectionTitle>Product reminder preferences updated</SectionTitle>
              </div>
              <CloseButton onClick={() => setShowCalendarSuccess(false)}>✕</CloseButton>
            </ReminderHeader>
            <ReminderBody>
              <ReminderInfo>
                Your product roadmap calendar preferences have been saved. You can now use the product calendar view and task board as part of the consistency layer.
              </ReminderInfo>
            </ReminderBody>
            <ReminderFooter>
              <PrimaryButton onClick={() => setShowCalendarSuccess(false)}>
                Continue
              </PrimaryButton>
            </ReminderFooter>
          </ReminderModal>
        </ModalOverlay>
      )}
    </Page>
  );
}

const Brief = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const Page = styled.div`
  min-height: 100vh;
  padding: 120px 20px 80px;
  background:
    radial-gradient(820px at 10% 0%, rgba(245, 158, 11, 0.12), transparent 60%),
    radial-gradient(880px at 90% 6%, rgba(56, 189, 248, 0.12), transparent 62%),
    linear-gradient(180deg, #06101d 0%, #040814 100%);
`;

const Shell = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const shimmer = `
  background: linear-gradient(
    90deg,
    rgba(148, 163, 184, 0.08) 0%,
    rgba(148, 163, 184, 0.18) 50%,
    rgba(148, 163, 184, 0.08) 100%
  );
  background-size: 200% 100%;
  animation: loadingShimmer 1.4s ease-in-out infinite;
`;

const LoadingHeroCard = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
`;

const LoadingSectionCard = styled.div`
  margin-top: 20px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42);
`;

const LoadingStatGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 560px) {
    grid-template-columns: 1fr;
  }
`;

const LoadingStatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const LoadingList = styled.div`
  margin-top: 18px;
  display: grid;
  gap: 14px;
`;

const LoadingListCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.58)),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const SkeletonPill = styled.div`
  width: 150px;
  height: 30px;
  border-radius: 999px;
  ${shimmer}
`;

const SkeletonTitle = styled.div`
  width: min(520px, 88%);
  height: 46px;
  margin-top: 18px;
  border-radius: 18px;
  ${shimmer}
`;

const SkeletonSectionTitle = styled.div`
  width: min(360px, 72%);
  height: 32px;
  margin-top: 12px;
  border-radius: 14px;
  ${shimmer}
`;

const SkeletonValue = styled.div`
  width: 86px;
  height: 30px;
  margin-top: 10px;
  border-radius: 12px;
  ${shimmer}
`;

const SkeletonText = styled.div`
  width: ${({ $w = "100%" }) => $w};
  height: 14px;
  margin-top: 12px;
  border-radius: 999px;
  ${shimmer}
`;

const EmptyCard = styled.div`
  display: grid;
  justify-items: start;
  gap: 12px;
  padding: 28px;
  border-radius: 28px;
  background: rgba(8, 16, 29, 0.94);
  border: 1px solid rgba(148, 163, 184, 0.14);

  h1 {
    margin: 0;
    color: #f8fafc;
  }

  p {
    margin: 0;
    color: #94a3b8;
    max-width: 58ch;
    line-height: 1.8;
  }
`;

const Hero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(280px, 0.95fr);
  gap: 20px;
  margin-bottom: 24px;
  align-items: stretch;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const HeroMain = styled.div`
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.82), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 28px 80px rgba(0, 0, 0, 0.42);
`;

const HeroSide = styled.div`
  display: grid;
  gap: 14px;
`;

const Eyebrow = styled.div`
  display: inline-flex;
  width: fit-content;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.1);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Title = styled.h1`
  margin: 14px 0 0;
  color: #f8fafc;
  font-size: 40px;
  font-weight: 1000;
  line-height: 1.05;
`;

const Sub = styled.p`
  margin-top: 12px;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.7;
  max-width: 760px;
`;

const MetaRow = styled.div`
  margin-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const MetaPill = styled.div`
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(15, 23, 42, 0.58);
  border: 1px solid rgba(148, 163, 184, 0.14);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 20px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(2, 6, 23, 0.5);
  border: 1px solid rgba(148, 163, 184, 0.12);
`;

const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 16px;
  border-radius: 999px;
  font-weight: 800;
  cursor: pointer;
`;

const PrimaryButton = styled(BaseButton)`
  border: none;
  background: linear-gradient(135deg, #f59e0b, #d97706);
  color: #111827;
`;

const SecondaryButton = styled(BaseButton)`
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
`;

const MetricCard = styled.div`
  padding: 22px;
  border-radius: 24px;
  background:
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.08)),
    rgba(15, 23, 42, 0.78);
  border: 1px solid rgba(245, 158, 11, 0.18);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.04);

  span {
    display: block;
    color: #fcd34d;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    font-size: 22px;
    line-height: 1.3;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 20px;

  @media (max-width: 1020px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  padding: 22px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  margin-bottom: 20px;
`;

const TodayCard = styled.div`
  margin-bottom: 20px;
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
`;

const TodayCardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const TodayTitle = styled.h2`
  margin: 8px 0 0;
  color: #f8fafc;
  font-size: clamp(1.45rem, 2.3vw, 2rem);
  line-height: 1.12;
`;

const TodaySummary = styled.p`
  margin: 10px 0 0;
  color: #cbd5e1;
  line-height: 1.75;
  max-width: 68ch;
`;

const TodayDayBadge = styled.div`
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(250, 204, 21, 0.14);
  border: 1px solid rgba(250, 204, 21, 0.24);
  color: #fde68a;
  font-size: 12px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

const TodayGrid = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const TodayBlock = styled.div`
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.58)),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    line-height: 1.55;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    line-height: 1.75;
    font-size: 13px;
  }
`;

const TodayLabel = styled.div`
  color: #fde68a;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const MiniList = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.75;
`;

const TodayActions = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.74);
  backdrop-filter: blur(10px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1100;
`;

const ReminderModal = styled.div`
  width: min(620px, 100%);
  border-radius: 28px;
  background: rgba(15, 23, 42, 0.96);
  border: 1px solid rgba(148, 163, 184, 0.16);
  overflow: hidden;
`;

const ReminderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 22px 16px;
  border-bottom: 1px solid rgba(148, 163, 184, 0.12);
`;

const ReminderBody = styled.div`
  padding: 20px 22px;
  display: grid;
  gap: 16px;
`;

const ReminderFooter = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  padding: 16px 22px 22px;
  border-top: 1px solid rgba(148, 163, 184, 0.12);
`;

const CloseButton = styled.button`
  border: none;
  background: rgba(148, 163, 184, 0.12);
  color: #e2e8f0;
  width: 36px;
  height: 36px;
  border-radius: 12px;
  cursor: pointer;
`;

const SettingRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
`;

const SettingCopy = styled.div`
  h4 {
    margin: 0;
    color: #f8fafc;
    font-size: 16px;
  }

  p {
    margin: 8px 0 0;
    color: #94a3b8;
    line-height: 1.7;
    font-size: 13px;
  }
`;

const ToggleShell = styled.label`
  input {
    width: 22px;
    height: 22px;
    accent-color: #f59e0b;
    cursor: pointer;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: rgba(148, 163, 184, 0.12);
`;

const SettingLabel = styled.div`
  color: #cbd5e1;
  font-size: 13px;
  font-weight: 800;
`;

const TimeInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(2, 6, 23, 0.34);
  color: #f8fafc;
`;

const ReminderInfo = styled.div`
  padding: 14px 16px;
  border-radius: 16px;
  background: rgba(56, 189, 248, 0.08);
  border: 1px solid rgba(56, 189, 248, 0.14);
  color: #bae6fd;
  font-size: 13px;
  line-height: 1.7;
`;

const FeatureList = styled.div`
  margin-top: 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
`;

const FeaturePill = styled.div`
  padding: 9px 12px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.18);
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 800;
`;

const StatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);

  span {
    display: block;
    color: #94a3b8;
    font-size: 12px;
    font-weight: 800;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    font-size: 28px;
    line-height: 1;
    font-weight: 1000;
  }
`;

const ConsistencyCard = styled.div`
  padding: 24px;
  border-radius: 30px;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.12), transparent 28%),
    linear-gradient(180deg, rgba(8, 16, 29, 0.96), rgba(5, 10, 22, 0.94));
  border: 1px solid rgba(148, 163, 184, 0.14);
  margin-bottom: 20px;
`;

const ConsistencyHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const SectionEyebrow = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const SectionTitle = styled.h2`
  margin: 8px 0 0;
  color: #f8fafc;
  font-size: 1.2rem;
`;

const LockedBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.24);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const ConsistencyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 18px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const ConsistencyActions = styled.div`
  margin-top: 18px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
`;

const ConsistencyItem = styled.div`
  padding: 18px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.54);
  border: 1px solid rgba(148, 163, 184, 0.14);

  svg {
    color: #7dd3fc;
  }

  strong {
    display: block;
    margin-top: 12px;
    color: #f8fafc;
    line-height: 1.5;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    line-height: 1.75;
    font-size: 13px;
  }
`;

const PhaseStack = styled.div`
  display: grid;
  gap: 16px;
`;

const PhaseCard = styled.div`
  padding: 24px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.76), rgba(2, 6, 23, 0.72)),
    radial-gradient(circle at top left, rgba(56, 189, 248, 0.08), transparent 34%);
  backdrop-filter: blur(18px) saturate(160%);
  -webkit-backdrop-filter: blur(18px) saturate(160%);
  border: 1px solid rgba(148, 163, 184, 0.12);
  box-shadow: 0 28px 70px rgba(0, 0, 0, 0.42),
    inset 0 1px 0 rgba(255, 255, 255, 0.04);
  transition: transform 0.35s ease, box-shadow 0.35s ease, border-color 0.35s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 36px 90px rgba(0, 0, 0, 0.46),
      inset 0 1px 0 rgba(255, 255, 255, 0.08);
    border-color: rgba(56, 189, 248, 0.3);
  }
`;

const PhaseHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  margin-bottom: 16px;
  cursor: pointer;
`;

const PhaseHeaderMain = styled.div`
  min-width: 0;
`;

const PhaseHeaderRight = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  flex: 0 0 auto;
`;

const PhaseSummary = styled.p`
  margin: 0;
  color: #94a3b8;
  line-height: 1.8;
`;

const WeekStack = styled.div`
  display: grid;
  gap: 14px;
`;

const WeekCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background:
    linear-gradient(135deg, rgba(15, 23, 42, 0.72), rgba(2, 6, 23, 0.58)),
    rgba(15, 23, 42, 0.7);
  border: 1px solid rgba(148, 163, 184, 0.12);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.32);
    border-color: rgba(56, 189, 248, 0.22);
  }
`;

const WeekHead = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 14px;
  cursor: pointer;

  strong {
    display: block;
    color: #f8fafc;
    font-size: 1rem;
  }

  p {
    margin: 6px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.7;
  }

  small {
    padding: 6px 10px;
    border-radius: 999px;
    background: rgba(56, 189, 248, 0.12);
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }
`;

const WeekHeadRight = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const FeatureFocus = styled.div`
  margin-top: 8px;
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 800;
`;

const DayGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
`;

const DayCard = styled.div`
  padding: 16px;
  border-radius: 18px;
  background: ${({ $locked }) =>
    $locked
      ? "rgba(15, 23, 42, 0.42)"
      : "linear-gradient(135deg, rgba(15, 23, 42, 0.68), rgba(2, 6, 23, 0.54))"};
  border: 1px solid
    ${({ $locked }) =>
      $locked ? "rgba(148, 163, 184, 0.08)" : "rgba(148, 163, 184, 0.12)"};
  opacity: ${({ $locked }) => ($locked ? 0.72 : 1)};
  transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: ${({ $locked }) => ($locked ? "none" : "translateY(-2px)")};
    border-color: ${({ $locked }) =>
      $locked ? "rgba(148, 163, 184, 0.08)" : "rgba(125, 211, 252, 0.24)"};
    box-shadow: ${({ $locked }) =>
      $locked ? "none" : "0 16px 34px rgba(0, 0, 0, 0.24)"};
  }

  span {
    display: block;
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    line-height: 1.5;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.75;
  }
`;

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  cursor: pointer;
`;

const DayHeaderLeft = styled.div`
  min-width: 0;
`;

const DayContent = styled.div`
  overflow: hidden;
`;

const ChevronDownWrap = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 12px;
  color: #94a3b8;
  background: rgba(148, 163, 184, 0.1);
  border: 1px solid rgba(148, 163, 184, 0.12);
  flex: 0 0 auto;
`;

const TopicLine = styled.div`
  margin-top: 8px;
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 800;
`;

const Deliverable = styled.div`
  margin-top: 10px;
  color: #cbd5e1;
  font-size: 13px;
  line-height: 1.7;

  strong {
    display: inline;
    margin: 0;
    font-size: inherit;
    color: #f8fafc;
  }
`;

const GuidanceBlock = styled.div`
  margin-top: 12px;
`;

const GuidanceTitle = styled.div`
  color: #e2e8f0;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const Checklist = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.75;
`;

const ReferenceList = styled.ul`
  margin: 10px 0 0;
  padding-left: 18px;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.75;
`;

const ReferenceLink = styled.a`
  color: #7dd3fc;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const AIHelperButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 14px;
  padding: 11px 14px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.96), rgba(250, 204, 21, 0.96));
  box-shadow: 0 16px 34px rgba(245, 158, 11, 0.22);
  color: #111827;
  font-size: 12px;
  font-weight: 900;
  cursor: pointer;
  border: 1px solid rgba(251, 191, 36, 0.4);
  transition: transform 0.18s ease, box-shadow 0.18s ease,
    border-color 0.18s ease;

  svg {
    color: #111827;
  }

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 20px 40px rgba(245, 158, 11, 0.28);
    border-color: rgba(251, 191, 36, 0.56);
  }
`;

const DayFooter = styled.div`
  margin-top: 14px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
`;

const DayTag = styled.div`
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.12);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 800;
`;

const DoneState = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #86efac;
  font-size: 12px;
  font-weight: 800;
`;

const CompleteButton = styled.button`
  border: none;
  border-radius: 999px;
  padding: 8px 12px;
  background: ${({ disabled }) =>
    disabled ? "rgba(71, 85, 105, 0.4)" : "rgba(245, 158, 11, 0.16)"};
  color: ${({ disabled }) => (disabled ? "#94a3b8" : "#fbbf24")};
  font-size: 12px;
  font-weight: 800;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
`;
